/**
 * Unit artwork registry and team-colour tinting.
 *
 * Each unit with art ships three files under `public/units/`:
 *   <slug>.webp             roster thumbnail, transparent, team panel neutral grey
 *   <slug>-thumb-mask.webp  the same render, opaque only over the team panel
 *   <slug>.glb              the 3D model, team panel neutralised in its texture
 *
 * The thumbnail and its mask come out of one render with one camera, so they
 * line up pixel for pixel and the browser can recolour the panel by
 * compositing rather than shipping a thumbnail per palette entry.
 */

export type UnitArtKey =
  | 'LIGHT_INFANTRY'
  | 'DEFENCE_BUNKER'
  | 'ATTACK_ROVER'
  | 'ATTACK_GUNSHIP'
  | 'SCOUT_BIKE'
  | 'DEFENCE_TURRET_WALKER'
  | 'SIEGE_ARTILLERY'
  | 'SIEGE_MORTAR_TANK'
  | 'SETTLER_CONVOY';

export interface UnitArt {
  /** Neutral-livery thumbnail. */
  thumb: string;
  /** Screen-space team-panel mask, aligned to `thumb`. */
  thumbMask: string;
  /** 3D model for the battle view. */
  model: string;
  /** Vehicle class, for tooltips and the asset manifest. */
  className: string;
}

function art(slug: string, className: string): UnitArt {
  return {
    thumb: `/units/${slug}.webp`,
    thumbMask: `/units/${slug}-thumb-mask.webp`,
    model: `/units/${slug}.glb`,
    className,
  };
}

/**
 * Only units whose art actually exists appear here — callers fall back to a
 * text-only row for the rest, so adding art is a one-line change.
 */
export const UNIT_ART: Partial<Record<UnitArtKey, UnitArt>> = {
  SCOUT_BIKE: art('unit-vehicle-light', 'Light scout / combat vehicle'),
  ATTACK_ROVER: art('unit-vehicle-medium', 'Medium vehicle (IFV)'),
  DEFENCE_BUNKER: art('unit-defence-bunker', 'Sector Holder squad'),
  ATTACK_GUNSHIP: art('unit-aircraft-gunship', 'Attack gunship (VTOL)'),
  LIGHT_INFANTRY: art('unit-infantry-light', 'Light infantry'),
  DEFENCE_TURRET_WALKER: art('unit-defence-walker', 'Defence turret walker'),
  SIEGE_ARTILLERY: art('unit-siege-artillery', 'Siege artillery'),
  SIEGE_MORTAR_TANK: art('unit-siege-mortar', 'Siege mortar tank'),
  SETTLER_CONVOY: art('unit-settler-convoy', 'Settler convoy'),
};

export function unitArtFor(unitType: string): UnitArt | null {
  return UNIT_ART[unitType as UnitArtKey] ?? null;
}

const tintCache = new Map<string, Promise<string | null>>();

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`failed to load ${src}`));
    img.src = src;
  });
}

async function composeTint(art: UnitArt, hex: string): Promise<string | null> {
  const out = document.createElement('canvas');
  const layer = document.createElement('canvas');
  const ctx = out.getContext('2d');
  const lctx = layer.getContext('2d');
  // jsdom and locked-down browsers have no 2D context. Bail before fetching
  // the images: the caller falls back to the neutral thumbnail, and under
  // jsdom an <img> load would never settle either way.
  if (!ctx || !lctx) return null;

  const [base, mask] = await Promise.all([
    loadImage(art.thumb),
    loadImage(art.thumbMask),
  ]);
  out.width = layer.width = base.naturalWidth;
  out.height = layer.height = base.naturalHeight;

  // Team colour multiplied by the panel's own greys, so wear and shading survive.
  lctx.drawImage(base, 0, 0);
  lctx.globalCompositeOperation = 'multiply';
  lctx.fillStyle = hex;
  lctx.fillRect(0, 0, layer.width, layer.height);
  // `multiply` fills the whole rect opaque — clip back to the vehicle, then to
  // the team panel. Both steps are needed; either alone leaves a colour slab.
  lctx.globalCompositeOperation = 'destination-in';
  lctx.drawImage(base, 0, 0);
  lctx.drawImage(mask, 0, 0);

  ctx.drawImage(base, 0, 0);
  ctx.drawImage(layer, 0, 0);
  return out.toDataURL('image/webp');
}

/**
 * A thumbnail with the team panel recoloured, or null when there is no art for
 * the unit or no canvas to composite on. Results are cached per unit+colour —
 * a battle screen asks for the same two liveries on every re-render.
 */
export function tintedUnitThumbnail(
  unitType: string,
  hex: string,
): Promise<string | null> {
  const art = unitArtFor(unitType);
  if (!art) return Promise.resolve(null);
  const key = `${unitType}|${hex}`;
  const cached = tintCache.get(key);
  if (cached) return cached;
  const pending = composeTint(art, hex).catch(() => null);
  tintCache.set(key, pending);
  return pending;
}

/** Test seam — the cache is process-wide and would leak between cases. */
export function clearUnitArtCache(): void {
  tintCache.clear();
}
